import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store";
import { brandingApi } from "@/features/settings/api";

const KEY = ["branding"] as const;

/**
 * Lightweight workspace settings — just the workspace name for now.
 *
 * Full white-label (logo, color, product-name override) is intentionally
 * NOT exposed here. That belongs to a paid agency tier and is tracked as
 * a separate feature.
 */
export default function WorkspaceTab() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const q = useQuery({
    queryKey: KEY,
    queryFn: brandingApi.get,
    staleTime: 30_000,
  });

  const [name, setName] = useState("");

  useEffect(() => {
    if (q.data) setName(q.data.name ?? "");
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => brandingApi.update({ name: name.trim() }),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      // Keep the auth store's cached tenant in sync so the sidebar
      // subtitle updates immediately.
      if (user?.tenant) {
        setUser({
          ...user,
          tenant: { ...user.tenant, name: data.name },
        });
      }
      toast.success("Workspace name updated");
    },
    onError: () => toast.error("Failed to update workspace name"),
  });

  if (q.isLoading || !q.data) return <Skeleton className="h-40 w-full" />;

  const dirty = name.trim() && name.trim() !== q.data.name;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Workspace</CardTitle>
        <CardDescription>
          The name of this workspace — shown to your team in the sidebar
          and on call records.
        </CardDescription>
      </CardHeader>
      <CardContent className="max-w-md space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="wn">Workspace name</Label>
          <Input
            id="wn"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Acme Corporation"
            maxLength={255}
          />
        </div>
        <Button
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}
